import React, { useContext } from 'react';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { AnalyticsProduct } from '@tink/tink-link-core/src/analytics-product';
import { ProductType } from '@tink/core/src/types';
import { ProductDimension, ProductNames } from '../../../models/product';
import { DeprecatedProductsTypes, ProductsType } from '../../../products/models/products';

import { getParameter } from '../../../utilities/urlParametersValidator';
import { ReportType } from '../../../reports/models/report';

export type ProductState = {
  productType: ProductType;
  productDimension: ProductDimension;
  productNames: ProductNames[];
};

export const ProductContext = React.createContext<ProductState>({
  productType: undefined as unknown as ProductType,
  productDimension: undefined as unknown as ProductDimension,
  productNames: undefined as unknown as ProductNames[],
});

export type ProductProviderProps = {
  initialProduct: {
    productType: ProductType;
    productDimension: ProductDimension;
  };
};

const validateAndFilterReportTypes = (value: string): value is ReportType => {
  return Object.values(ReportType).includes(value as ReportType);
};

const validateAndFilterProductTypes = (
  value: string
): value is ProductsType | DeprecatedProductsTypes => {
  return (
    Object.values(ProductsType).includes(value as ProductsType) ||
    Object.values(DeprecatedProductsTypes).includes(value as DeprecatedProductsTypes)
  );
};

export const defaultGetReportsProductNames = (getQueryParam = getParameter): ProductNames[] => {
  const reportTypes = (getQueryParam(TinkLinkUrlParameter.REPORT_TYPES) || '')
    .split(',')
    .filter(validateAndFilterReportTypes);

  if (reportTypes.length === 0) {
    return [ProductNames.PRODUCT_UNKNOWN];
  }

  return reportTypes.map((reportType) => {
    switch (reportType) {
      case ReportType.ACCOUNT_VERIFICATION_REPORT:
        return ProductNames.PRODUCT_ACCOUNT_CHECK;
      case ReportType.TRANSACTION_REPORT:
        return ProductNames.PRODUCT_TRANSACTIONS;
      case ReportType.INCOME_CHECK_REPORT:
        return ProductNames.PRODUCT_INCOME_CHECK;
      case ReportType.RISK_INSIGHTS_REPORT:
        return ProductNames.PRODUCT_RISK_INSIGHTS;
      case ReportType.BUSINESS_ACCOUNT_VERIFICATION_REPORT:
        return ProductNames.PRODUCT_BUSINESS_ACCOUNT_CHECK;
      case ReportType.EXPENSE_CHECK_REPORT:
        return ProductNames.PRODUCT_EXPENSE_CHECK;
      default:
        return ProductNames.PRODUCT_UNKNOWN;
    }
  });
};

const defaultGetProductsNames = (getQueryParam = getParameter): ProductNames[] => {
  const productTypes = (getQueryParam(TinkLinkUrlParameter.PRODUCTS) || '')
    .split(',')
    .filter(validateAndFilterProductTypes);

  return productTypes.map((product) => {
    switch (product) {
      case ProductsType.ACCOUNT_CHECK:
        return ProductNames.PRODUCT_ACCOUNT_CHECK;
      case ProductsType.TRANSACTIONS:
        return ProductNames.PRODUCT_TRANSACTIONS;
      case DeprecatedProductsTypes.ASSETS:
      case ProductsType.INVESTMENTS:
        return ProductNames.PRODUCT_INVESTMENTS;
      case DeprecatedProductsTypes.LIABILITIES:
      case ProductsType.LOANS:
        return ProductNames.PRODUCT_LOANS;
      default:
        return ProductNames.PRODUCT_UNKNOWN;
    }
  });
};

export const getAnalyticsProductsFromQueryProducts = (
  getQueryParam = getParameter
): AnalyticsProduct[] => {
  const productTypes = (getQueryParam(TinkLinkUrlParameter.PRODUCTS) || '')
    .split(',')
    .filter(validateAndFilterProductTypes);

  if (productTypes.length === 0) {
    return [AnalyticsProduct.UNKNOWN];
  }

  return productTypes.map((product) => {
    switch (product) {
      case ProductsType.ACCOUNT_CHECK:
        return AnalyticsProduct.ACCOUNT_CHECK;
      case ProductsType.TRANSACTIONS:
        return AnalyticsProduct.TRANSACTIONS;
      case DeprecatedProductsTypes.ASSETS:
      case ProductsType.INVESTMENTS:
        return AnalyticsProduct.INVESTMENTS;
      case DeprecatedProductsTypes.LIABILITIES:
      case ProductsType.LOANS:
        return AnalyticsProduct.LOANS;
      default:
        return AnalyticsProduct.UNKNOWN;
    }
  });
};

export const getAnalyticsProductsFromReportTypes = (
  getQueryParam = getParameter
): AnalyticsProduct[] => {
  const reportTypes = (getQueryParam(TinkLinkUrlParameter.REPORT_TYPES) || '')
    .split(',')
    .filter(validateAndFilterReportTypes);

  if (reportTypes.length === 0) {
    return [AnalyticsProduct.UNKNOWN];
  }

  return reportTypes.map((reportType) => {
    switch (reportType) {
      case ReportType.ACCOUNT_VERIFICATION_REPORT:
        return AnalyticsProduct.ACCOUNT_CHECK;
      case ReportType.TRANSACTION_REPORT:
        return AnalyticsProduct.TRANSACTIONS;
      case ReportType.INCOME_CHECK_REPORT:
        return AnalyticsProduct.INCOME_CHECK;
      case ReportType.RISK_INSIGHTS_REPORT:
        return AnalyticsProduct.RISK_INSIGHTS;
      case ReportType.BUSINESS_ACCOUNT_VERIFICATION_REPORT:
        return AnalyticsProduct.BUSINESS_ACCOUNT_CHECK;
      case ReportType.EXPENSE_CHECK_REPORT:
        return AnalyticsProduct.EXPENSE_CHECK;
      default:
        return AnalyticsProduct.UNKNOWN;
    }
  });
};

export const getProductNames = (
  productType: ProductType,
  getReportsProductNames = defaultGetReportsProductNames,
  getProductsNames = defaultGetProductsNames
): ProductNames[] => {
  switch (productType) {
    case ProductType.AUTHORIZE:
      return [ProductNames.PRODUCT_ACCOUNT_AGGREGATION];
    case ProductType.PAY:
      return [ProductNames.PRODUCT_PAYMENT_INITIATION];
    case ProductType.CREDENTIALS:
      return [ProductNames.PRODUCT_ACCOUNT_AGGREGATION];
    case ProductType.ACCOUNT_CHECK:
      return [ProductNames.PRODUCT_ACCOUNT_CHECK];
    case ProductType.INCOME_CHECK:
      return [ProductNames.PRODUCT_INCOME_CHECK];
    case ProductType.RISK_INSIGHTS:
      return [ProductNames.PRODUCT_RISK_INSIGHTS];
    case ProductType.EXPENSE_CHECK:
      return [ProductNames.PRODUCT_EXPENSE_CHECK];
    case ProductType.TRANSACTIONS:
      return [ProductNames.PRODUCT_TRANSACTIONS];
    case ProductType.BUSINESS_TRANSACTIONS:
      return [ProductNames.PRODUCT_BUSINESS_TRANSACTIONS];
    case ProductType.REPORTS:
      return getReportsProductNames();
    case ProductType.BUSINESS_ACCOUNT_CHECK:
      return [ProductNames.PRODUCT_BUSINESS_ACCOUNT_CHECK];
    case ProductType.PRODUCTS:
      return getProductsNames();
    default:
      return [ProductNames.PRODUCT_UNKNOWN];
  }
};

export const ProductProvider: React.FC<ProductProviderProps> = ({
  children,
  initialProduct: { productType, productDimension },
}) => {
  const productNames: ProductNames[] = getProductNames(productType);

  return (
    <ProductContext.Provider value={{ productType, productDimension, productNames }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProduct = () => useContext(ProductContext);
